import { useState } from "react";
import {
  Dialog,
  DialogTrigger,
  DialogSurface,
  DialogBody,
  DialogActions,
  Button,
  makeStyles,
  Spinner,
} from "@fluentui/react-components";
import { Checkmark20Regular, Image20Regular } from "@fluentui/react-icons";
import { useTranslation } from "react-i18next";
import { Image } from "../../models/Image";
import { useGetAllPublished } from "../../hooks/useImages";
import { TextSortAscending24Regular } from "@fluentui/react-icons";
import { TextSortDescending24Regular } from "@fluentui/react-icons";
import {
  useQueryClient,
  useMsal,
  getAccessTokenUsingMsal,
} from "@skanska/vsa-client-auth";
import { apiConfig } from "../../config/AppConfig";
import { ApiEndpoints } from "../../models/ApiEndpoints";
import { VsaSearchBox } from "../VsaSearchBox";
import { Paginate } from "../Paginate";

const useStyles = makeStyles({
  root: {
    display: "flex",
    flexDirection: "column",
    gap: "16px",
    margin: "16px",
    height: "550px",
  },
  dialogtitle: {
    marginLeft: "16px",
  },
  imageContainer: {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
  },
  searchRow: {
    display: "flex",
    alignItems: "center",
    alignContent: "center",
    gap: "8px",
  },
  searchInput: {
    width: "400px",
  },
  imageButton: {
    margin: "10px 0",
    border: "none",
  },
  imageList: {
    overflowY: "auto",
  },
  checkBoxGroup: {
    borderBottom: "1px solid #ddd",
  },
  imageItem: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "6px 8px",
    cursor: "pointer",
    borderBottom: "1px solid #ddd",
    "&:hover": {
      backgroundColor: "#eee",
    },
  },
  imageName: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  imageInfo: {
    display: "flex",
    flexDirection: "column",
    gap: "2px",
  },
  imageTitle: {
    fontWeight: "500",
  },
  imageDetails: {
    fontSize: "12px",
    color: "#666",
  },
});

type ImagePickerDialogProps = {
  selectedImages?: string[];
  onSelect: (image: Image) => void;
  onRemove?: (imageId: string) => void;
  tooltipLabel?: string;
  externalOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
};

export default function ImagePickerDialog({
  selectedImages = [],
  onSelect,
  onRemove,
  tooltipLabel,
  externalOpen,
  onOpenChange,
}: ImagePickerDialogProps) {
  const classes = useStyles();
  const { t } = useTranslation();
  const {
    data: imagesResult,
    isLoading,
    isRefetching,
    setSortByAscending,
    setQuery,
    setCurrentPage,
    sortByAscending,
    query,
  } = useGetAllPublished("title", false, [], 50, true);

  const queryClient = useQueryClient();
  const { instance, inProgress } = useMsal();

  const [internalOpen, setInternalOpen] = useState(false);
  const open = externalOpen !== undefined ? externalOpen : internalOpen;
  const setOpen = onOpenChange || setInternalOpen;
  const [fetchingImage, setFetchingImage] = useState(false);

  const handleImageSelect = async (pickedImage: Image) => {
    try {
      setFetchingImage(true);

      const fullImage = await queryClient.fetchQuery({
        queryKey: [`image-${pickedImage.versionId}`],
        queryFn: async () => {
          // Get authentication token
          const token = await getAccessTokenUsingMsal(
            inProgress,
            instance,
            apiConfig.loginScopes,
            apiConfig.tenantId
          );

          // Fetch full image data
          const response = await fetch(
            `${apiConfig.apiUrl}/${ApiEndpoints.Image}/${pickedImage.versionId}`,
            {
              headers: {
                Authorization: `Bearer ${token}`,
                "Content-Type": "application/json",
              },
            }
          );
          return response.json();
        },
      });

      onSelect(fullImage);
      setOpen(false);
    } catch (error) {
      console.error("Error fetching full image data:", error);
      setOpen(false);
    } finally {
      setFetchingImage(false);
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={(_, data) => setOpen(data.open)}>
        <>
          {externalOpen === undefined && (
            <DialogTrigger>
              <Button
                className={classes.imageButton}
                icon={<Image20Regular />}
                title={tooltipLabel}
                appearance="subtle"
              ></Button>
            </DialogTrigger>
          )}
        </>
        <DialogSurface>
          <DialogBody className={classes.root}>
            {isLoading || isRefetching || fetchingImage || !imagesResult ? (
              <Spinner />
            ) : (
              <>
                <div className={classes.imageContainer}>
                  <div className={classes.searchRow}>
                    <VsaSearchBox
                      placeholder={t("searchImage")}
                      defaultValue={query}
                      onSearch={setQuery}
                      disableQueryParameter
                    />
                    <Button
                      size="medium"
                      onClick={() => setSortByAscending((sort) => !sort)}
                      icon={
                        sortByAscending ? (
                          <TextSortAscending24Regular />
                        ) : (
                          <TextSortDescending24Regular />
                        )
                      }
                      aria-label={
                        sortByAscending ? t("unsortAlpha") : t("sortAlpha")
                      }
                    />
                  </div>
                </div>
                <div className={classes.imageList}>
                  {imagesResult.items.map((image) => (
                    <div
                      key={image.versionId}
                      className={classes.imageItem}
                      onClick={() => handleImageSelect(image)}
                    >
                      <span className={classes.imageName}>
                        <div className={classes.imageInfo}>
                          <div className={classes.imageTitle}>
                            {image.title}
                          </div>
                          <div className={classes.imageDetails}>
                            {image.fileName}
                          </div>
                        </div>
                      </span>
                      {selectedImages.includes(image.versionId) && onRemove && (
                        <Checkmark20Regular
                          onClick={(e) => {
                            e.stopPropagation();
                            onRemove(image.versionId);
                          }}
                        />
                      )}
                    </div>
                  ))}
                  {imagesResult.items.length === 0 && <p>{t("noResults")}</p>}
                </div>
                {imagesResult.numberOfPages > 1 && (
                  <Paginate
                    pageCount={imagesResult.numberOfPages}
                    onPageClick={setCurrentPage}
                    defaultPage={imagesResult.pageNumber}
                  />
                )}
              </>
            )}
          </DialogBody>
          <DialogActions>
            <Button onClick={() => setOpen(false)}>{t("close")}</Button>
          </DialogActions>
        </DialogSurface>
      </Dialog>
    </>
  );
}
